import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import DualActionFooter from '@/components/feature/DualActionFooter';
import HowWeVetEngineers from '@/components/feature/HowWeVetEngineers';

const generateServiceSchema = () => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Hire Python Developers",
  "provider": { "@type": "Organization", "name": "Thoughtic Solutions", "url": "https://thoughtic.com" },
  "description": "Hire pre-vetted Python, Django, and FastAPI developers in 48 hours. Remote dedicated engineers for backends, data pipelines, and ML systems with 14-day risk-free trial.",
  "serviceType": "IT Staff Augmentation",
  "url": "https://thoughtic.com/hire-python-developers",
  "areaServed": { "@type": "Country", "name": "Worldwide" }
});

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

/* ── Hero ── */
function Hero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-16 md:pt-40 md:pb-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20technology%20background%20with%20flowing%20Python%20code%20and%20data%20visualization%20charts%2C%20deep%20navy%20blue%20with%20amber%20yellow%20and%20teal%20green%20accents%2C%20futuristic%20data%20science%20aesthetic%2C%20floating%20code%20syntax%20highlighting%20in%20warm%20tones%2C%20soft%20bokeh%20light%20particles%2C%20professional%20minimal%20composition%20suitable%20for%20website%20hero%20banner%20with%20white%20text%20overlay%2C%20modern%20backend%20development%20theme&width=1600&height=900&seq=python-hero-1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-code-s-slash-line text-amber-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Python Specialists</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Hire Python Developers
            <br />
            <span className="text-primary-400">In 48 Hours</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-2xl mx-auto"
          >
            Pre-vetted Python engineers for web backends, data pipelines, ML systems, and automation. Remote, dedicated, and matched to your stack in under two days.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/talent"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Find Python Developers
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/how-it-works"
              className="px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              See How It Works
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

/* ── Stats Bar ── */
function StatsBar() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const stats = [
    { value: '110+', label: 'Python Engineers', icon: 'ri-code-s-slash-line' },
    { value: '4.8yr', label: 'Avg. Experience', icon: 'ri-medal-line' },
    { value: '48h', label: 'First Matches', icon: 'ri-time-line' },
    { value: '92%', label: 'Hire Rate', icon: 'ri-thumb-up-line' },
  ];

  return (
    <section className="py-10 md:py-14 bg-white border-b border-gray-100" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + i * 0.08, ...springTransition }}
              className="text-center"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50 mx-auto mb-2">
                <i className={`${stat.icon} text-amber-600 text-lg`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-navy-900 mb-0.5">{stat.value}</div>
              <div className="text-xs text-gray-500 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Why Python Engineers ── */
function WhyPythonEngineers() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const reasons = [
    {
      icon: 'ri-shield-check-line',
      title: 'Pre-Vetted Python Specialists',
      desc: 'Every Python developer passes a 4-stage screening including live coding, system design with Flask and FastAPI patterns, and data processing challenges. Only the top 12% make it through.',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Full-Stack Python Fluency',
      desc: 'Our engineers command Django, FastAPI, Flask, Celery, SQLAlchemy, Pandas, and NumPy — plus cloud deployment on AWS Lambda and GCP. Not just script writers.',
    },
    {
      icon: 'ri-team-line',
      title: 'Team Integration Ready',
      desc: 'They join your standups, use your tooling, and follow your conventions. Full-time dedicated engineers who function as extensions of your in-house team from day one.',
    },
    {
      icon: 'ri-loop-left-line',
      title: 'Risk-Free Replacement',
      desc: 'Not the right fit? We replace any Python engineer within the first 14 days at zero cost. No questions asked, no paperwork delays — just the right talent, fast.',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-code-s-slash-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Why Hire Python Developers Through Thoughtic</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Python Talent,
            <span className="text-primary-600"> Without the Hiring Hassle</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Stop sifting through hundreds of resumes. We have already identified, tested, and verified the Python engineers who can ship production code on your stack from week one.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {reasons.map((r, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-amber-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-amber-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${r.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{r.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{r.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Python Tech Stack ── */
function PythonTechStack() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const skills = [
    { name: 'Python', level: 'Expert', color: 'bg-amber-500' },
    { name: 'Django', level: 'Expert', color: 'bg-emerald-600' },
    { name: 'FastAPI', level: 'Expert', color: 'bg-teal-500' },
    { name: 'Flask', level: 'Advanced', color: 'bg-gray-600' },
    { name: 'Celery', level: 'Advanced', color: 'bg-green-500' },
    { name: 'Pandas', level: 'Expert', color: 'bg-amber-600' },
    { name: 'NumPy', level: 'Advanced', color: 'bg-cyan-600' },
    { name: 'SQLAlchemy', level: 'Advanced', color: 'bg-stone-600' },
    { name: 'TensorFlow', level: 'Advanced', color: 'bg-orange-500' },
    { name: 'PyTorch', level: 'Advanced', color: 'bg-rose-500' },
    { name: 'AWS Lambda', level: 'Advanced', color: 'bg-yellow-600' },
    { name: 'Docker', level: 'Expert', color: 'bg-sky-500' },
  ];

  return (
    <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-amber-100/40 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-primary-100/40 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 shadow-sm"
          >
            <i className="ri-code-s-slash-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Technology Coverage</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            The Python
            <span className="text-primary-600"> Ecosystem</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            When you hire Python developers through Thoughtic, you get engineers fluent in the entire modern Python stack — from web frameworks to data science and machine learning.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
          {skills.map((skill, i) => (
            <motion.div
              key={skill.name}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.04, ...springTransition }}
              className="group bg-white rounded-xl p-4 border border-gray-100 hover:border-amber-200 hover:shadow-md transition-all duration-300 text-center"
            >
              <div className={`w-3 h-3 rounded-full ${skill.color} mx-auto mb-3`}></div>
              <div className="text-sm font-bold text-navy-900 mb-0.5">{skill.name}</div>
              <div className="text-[10px] text-gray-500 font-medium">{skill.level}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Vetting Process ── */
function VettingProcess() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const steps = [
    {
      num: '01',
      title: 'Resume & Portfolio Review',
      desc: 'We verify 3+ years of production Python experience, open-source contributions, and shipped backend or data pipeline evidence.',
    },
    {
      num: '02',
      title: 'Live Technical Interview',
      desc: 'A 90-minute session covering Python patterns, API design with FastAPI or Django, ORM optimization, and async processing decisions.',
    },
    {
      num: '03',
      title: 'Code Challenge',
      desc: 'Candidates build a REST API with authentication, database modeling, and background task processing — reviewed by senior staff engineers.',
    },
    {
      num: '04',
      title: 'Communication & Culture Fit',
      desc: 'We assess English fluency, remote work discipline, and ability to articulate technical decisions to non-technical stakeholders.',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 shadow-sm"
          >
            <i className="ri-shield-check-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Quality Assurance</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            How We Vet
            <span className="text-primary-600"> Python Developers</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Only 12% of Python applicants pass our full screening. Here is the 4-stage process every engineer endures before reaching your shortlist.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.1, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="text-3xl font-black text-primary-200 mb-3">{step.num}</div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{step.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Case Study Snippet ── */
function CaseStudySnippet() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <section className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ ...springTransition, duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4">
              <i className="ri-briefcase-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
              <span className="text-sm font-medium text-white/80">Client Success</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4">
              FinTech Startup Scales
              <span className="text-primary-400"> Data Pipeline</span> with Python Engineers
            </h2>
            <p className="text-base md:text-lg text-white/70 leading-relaxed mb-6">
              A fast-growing FinTech needed to rebuild their transaction processing pipeline from batch to real-time streaming. We matched them with 3 senior Python engineers — including a FastAPI specialist and a Pandas/NumPy expert — who reduced data latency from 6 hours to under 90 seconds.
            </p>
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div>
                <div className="text-2xl font-bold text-primary-400">5 Days</div>
                <div className="text-xs text-white/60">To first hire</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-400">3 Engineers</div>
                <div className="text-xs text-white/60">Placed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-400">99.97%</div>
                <div className="text-xs text-white/60">Pipeline uptime</div>
              </div>
            </div>
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Read Full Case Study
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, ...springTransition, duration: 0.7 }}
            className="relative"
          >
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20fintech%20data%20dashboard%20interface%20displayed%20on%20large%20monitor%20screen%20in%20minimalist%20office%20setting%2C%20Python%20backend%20data%20visualization%20with%20real-time%20charts%20and%20graphs%2C%20dark%20mode%20interface%20with%20amber%20and%20teal%20accents%2C%20professional%20product%20photography%20style%2C%20soft%20ambient%20lighting%2C%20shallow%20depth%20of%20field&width=800&height=600&seq=python-case-1&orientation=landscape"
              alt="Python data pipeline case study"
              className="rounded-2xl shadow-2xl shadow-navy-900/30 w-full"
            />
            <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-white rounded-xl p-4 shadow-xl border border-gray-100">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
              </div>
              <p className="text-xs text-gray-600">"Transformed our entire data infrastructure."</p>
              <p className="text-[10px] text-gray-400 mt-0.5">— VP Engineering, FinTech Startup</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ── Final CTA ── */
function FinalCTA() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 right-0 w-[400px] h-[400px] bg-amber-100/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 left-0 w-[400px] h-[400px] bg-primary-100/30 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-6 shadow-sm">
            <i className="ri-code-s-slash-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Ready to Ship Faster?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6">
            Hire Python Developers
            <span className="text-primary-600"> This Week</span>
          </h2>
          <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 md:mb-10">
            Tell us what you are building and we will hand-pick Python engineers who match your stack, timeline, and team culture. First candidates in 48 hours.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/talent"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Get Python Candidates
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/pricing"
              className="px-8 py-4 border-2 border-navy-900/20 text-navy-900 text-base font-semibold rounded-xl hover:bg-navy-900 hover:text-white hover:border-navy-900 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              View Pricing
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function HirePythonDevelopersPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = generateServiceSchema();
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <PageLayout
      title="Hire Python Developers — Django, FastAPI & ML Engineers"
      description="Hire pre-vetted Python, Django, and FastAPI developers in 48 hours. Remote dedicated engineers for backends, data pipelines, and ML systems. 14-day risk-free trial."
      keywords="hire Python developers, Django developers for hire, FastAPI engineers, Python data engineers, ML engineers Python, Python backend developers, remote Python developers, Python staff augmentation, Celery Pandas engineers"
    >
      <Hero />
      <StatsBar />
      <WhyPythonEngineers />
      <PythonTechStack />
      <HowWeVetEngineers
        accentColor="amber"
        heading="How We Vet Python Developers"
        specialtyNote="For Python roles, our code challenge specifically tests API design with FastAPI or Django, ORM optimization, async processing patterns, and data pipeline architecture."
      />
      <CaseStudySnippet />
      <FinalCTA />
      <DualActionFooter />
    </PageLayout>
  );
}